import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { JWT_TOKEN, REFRESH_TOKEN, getLoginUrlWithRedirect } from "../constants/auth";
import {
  cookiesCleanup,
  saveRefreshToken,
  saveUserInfo,
  saveUserToken,
} from "../utils/authStorage";
import { ssoRefreshToken } from "../services/ssoAuthService";
import { logger } from "../utils/logger";

interface AuthContextValue {
  isAuthenticated: boolean;
  isResolving: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(Cookies.get(JWT_TOKEN)));
  const [isResolving, setIsResolving] = useState(() => !Cookies.get(JWT_TOKEN));
  // Refresh tokens are single-use (rotated server-side on every refresh call), so
  // React StrictMode's dev-only double-invoke of this effect would otherwise fire
  // the same refresh token twice — the first call succeeds and rotates it, the
  // second then 401s on the now-stale token. Guard with a ref (not state, so it
  // persists across the mount/unmount/remount without triggering a re-render) to
  // ensure only one refresh attempt per token ever actually fires.
  const hasAttemptedRefresh = useRef(false);

  useEffect(() => {
    if (Cookies.get(JWT_TOKEN)) {
      setIsResolving(false);
      return;
    }

    if (hasAttemptedRefresh.current) {
      return;
    }
    hasAttemptedRefresh.current = true;

    let cancelled = false;

    async function getAccessTokenSilently() {
      const refreshToken = Cookies.get(REFRESH_TOKEN);

      if (!refreshToken) {
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
        return;
      }

      try {
        const data = await ssoRefreshToken({ refreshToken });
        if (data.status === 0) {
          saveUserToken(data.accessToken);
          saveRefreshToken(data.refreshToken, data.refreshTokenExpiresAt);

          const infoPayload = {
            applications: data.applications,
            operatorId: data.operatorId,
            name: data.name,
            location: data.location,
          };
          saveUserInfo(btoa(JSON.stringify(infoPayload)));

          if (!cancelled) {
            setIsAuthenticated(true);
            setIsResolving(false);
          }
        } else {
          cookiesCleanup();
          window.location.href = getLoginUrlWithRedirect();
        }
      } catch (error) {
        logger.error("Silent token refresh failed", error);
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
      }
    }

    getAccessTokenSilently();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ isAuthenticated, isResolving }), [isAuthenticated, isResolving]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
